import { apiClient } from '../common/utils/apiClient';
import { API_CONFIG } from '../config/api';
import { sessionStorageUtils } from '../common/utils/sessionStorage';
import { VanchiNameCreatePayload } from '../types';

export const vanchiNameService = {
  // Get all Vanchi (from /vanchi endpoint)
  getVanchiNames: async (): Promise<{ data: any[]; rowCount?: number }> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    
    const response = await apiClient.get<any>(API_CONFIG.ENDPOINTS.VANCHI, token);
    
    // Normalize response to always return { data: [], rowCount: number }
    if (Array.isArray(response)) {
      return { data: response, rowCount: response.length };
    }
    if (response && Array.isArray(response.data)) {
      return { data: response.data, rowCount: response.rowCount || response.data.length };
    }
    return { data: [], rowCount: 0 };
  },

  // Create a new Vanchi - matches Postman collection payload
  createVanchiName: async (payload: VanchiNameCreatePayload): Promise<any> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    
    return apiClient.post<any>(API_CONFIG.ENDPOINTS.CREATE_VANCHI, payload, token);
  },

  // Update an existing VanchiName by TblID
  updateVanchiName: async (tblid: string, data: any, existingRowData: any, nadaList?: any[], userId?: string, isVanchiNoForm?: boolean): Promise<any> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    
    // Determine the correct nadacd and nadaname
    let nadacd = existingRowData.nadacd;
    let nadaname = existingRowData.nadaname;
    
    // If nadaId is provided and different from existing, update both nadacd and nadaname
    if (data.nadaId && data.nadaId !== existingRowData.nadacd) {
      nadacd = data.nadaId;
      // Find the corresponding nada name from the nada list
      if (nadaList) {
        const selectedNada = nadaList.find(nada => nada.valuesetcode === data.nadaId);
        nadaname = selectedNada?.valuesetnameen || selectedNada?.valuesetnameml || nadaname;
      }
    }
    
    // Determine vanchino value based on form type
    let vanchino = existingRowData.vanchino; // Default to existing value
    if (isVanchiNoForm && data.vanchiNumber) {
      // If this is from vanchi no form, use the provided vanchi number
      vanchino = data.vanchiNumber;
    }
    
    // Use existing row data and only update changed fields - matches API structure
    const updatePayload: any = {
      nadacd: nadacd,
      nadaname: nadaname,
      vanchicd: existingRowData.vanchicd, // Keep existing vanchicd from row
      vanchiname: data.vanchiName || existingRowData.vanchiname,
      vanchino: vanchino, // Use determined vanchino value
      isactive: existingRowData.isactive, // Keep existing isactive from row
      modifiedby: userId || 'ADMIN' // Use provided userId or fallback to 'ADMIN'
    };
    
    return apiClient.put<any>(API_CONFIG.ENDPOINTS.VANCHI_BY_TBLID(tblid), updatePayload, token);
  },

  // Update Vanchi by VanchiCD - additional method from Postman collection
  updateVanchiByCode: async (vanchicd: string, data: {
    vanchiname?: string;
    vanchino?: string;
    modifiedby: string;
  }): Promise<any> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    
    return apiClient.put<any>(API_CONFIG.ENDPOINTS.VANCHI_BY_CODE(vanchicd), data, token);
  },

  // Get Vanchi by VanchiCD - from Postman collection
  getVanchiByCode: async (vanchicd: string): Promise<any> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    
    return apiClient.get<any>(API_CONFIG.ENDPOINTS.VANCHI_BY_CODE(vanchicd), token);
  },

  // Get Vanchi by NadaCD - from Postman collection
  getVanchiByNada: async (nadacd: string): Promise<any> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    
    return apiClient.get<any>(API_CONFIG.ENDPOINTS.VANCHI_BY_NADA(nadacd), token);
  },

  // Delete a VanchiName (soft delete by setting isActive to false)
  deleteVanchiName: async (tblid: string): Promise<void> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    
    const deletePayload = {
      isactive: 'N',
      modifiedby: 'ADMIN'
    };
    
    return apiClient.put<any>(API_CONFIG.ENDPOINTS.VANCHI_BY_TBLID(tblid), deletePayload, token);
  },

  // Toggle VanchiName active status
  toggleVanchiNameStatus: async (tblid: string, currentStatus: string): Promise<any> => {
    const token = sessionStorageUtils.getToken();
    if (!token) throw new Error('No authentication token');
    
    const togglePayload = {
      isactive: currentStatus === 'Y' ? 'N' : 'Y',
      modifiedby: 'ADMIN'
    };
    
    return apiClient.put<any>(API_CONFIG.ENDPOINTS.VANCHI_BY_TBLID(tblid), togglePayload, token);
  }
};
